import {Injectable} from '@angular/core';
import {firstValueFrom, from, Observable, ReplaySubject, switchMap} from 'rxjs';
import {TrainingService} from './training.service';
import {HttpClient} from '@angular/common/http';
import {AuthContextService} from './services/auth-context.service';
import {ProgressSummary} from './domain/interfaces/progress-summary.interface';

@Injectable({
  providedIn: 'root'
})
export class ProgressService {

  progressSummarySubject = null;
  courseType = null;

  constructor(
    private trainingService: TrainingService,
    private httpClient: HttpClient,
    private authContextService: AuthContextService
  ) {

    this.authContextService.onLogout().subscribe(
      value => {
        console.log('ProgressService:onLogout');
        this.progressSummarySubject = null;
        this.courseType = null;
      }
    );


  }

  async getApi(): Promise<any> {
    return await firstValueFrom(this.trainingService.load());
  }

  getUserCourse(courseType: string): Observable<any> {
    console.log('ProgressService:getUserCourse');
    return from(this.getApi()).pipe(
      switchMap(api =>
        this.httpClient.get(api.getRoute('getUserCourse').replace('{webAppId}', courseType))
      )
    );
  }

  getCertificationRequests(courseType: string): Observable<any> {
    return from(this.getApi()).pipe(
      switchMap(api =>
        this.httpClient.get(api.getRoute('getCertificationRequests').replace('{webAppId}', courseType))
      )
    );
  }

  getUserProgressSummary(courseType: string, refresh: boolean = false): Observable<ProgressSummary> {
    console.log('ProgressService:getUserProgressSummary');

    if (this.progressSummarySubject && !refresh && this.courseType === courseType) {
      return this.progressSummarySubject;
    }

    if (!this.progressSummarySubject || this.courseType !== courseType) {
      this.progressSummarySubject = new ReplaySubject(1);
      this.courseType = courseType;
    }

    this.getApi().then(api => {
      this.httpClient.get(api.getRoute('getUserProgressSummary').replace('{webAppId}', courseType))
        .subscribe(value => {
          this.progressSummarySubject.next(value);
        });
    });

    return this.progressSummarySubject;
  }

  getUserProgressSummaryPercentageComplete(courseType: string): Observable<any> {
    return from(this.getApi()).pipe(
      switchMap(api =>
        this.httpClient.get(api.getRoute('getUserProgressSummary').replace('{webAppId}', courseType))
      )
    );
  }

  getLatestCourse(): Observable<any> {
    console.log('ProgressService:getVolumeProgress');
    return from(this.getApi()).pipe(
      switchMap(api =>
        this.httpClient.get(api.getRoute('getLastAccessedCourse'))
      )
    );
  }

  getVolumeProgress(courseType: string, volumeId: number): Observable<any> {
    console.log('ProgressService:getVolumeProgress');
    return from(this.getApi()).pipe(
      switchMap(api =>
        this.httpClient.get(api.getRoute('getVolumeProgress').replace('{webAppId}', courseType).replace('{volumeId}', volumeId))
      )
    );
  }

  getNextVideoSection(courseType: string): Observable<any> {
    console.log('GetNextVideoSection');
    return from(this.getApi()).pipe(
      switchMap(api =>
        this.httpClient.get(api.getRoute('getNextVideoSection').replace('{webAppId}', courseType))
      )
    );
  }

  /*
  async loadMany(courseType: string): Promise<any> {
    const api = await this.getApi();
    return firstValueFrom(this.httpClient.get(api.getRoute('getCourseProgress').replace('{webAppId}', courseType)));
  }
  */

  async findInvitationV2(token: string) {
    const api = await this.getApi();
    return firstValueFrom(this.httpClient.get(api.getRoute('cfi-student-findInvitation').replace('{uuid}', token)));
  }

  async acceptInvitationV2(token: string) {
    const api = await this.getApi();
    return this.httpClient.put(api.getRoute('cfi-student-acceptInvitation').replace('{uuid}', token), {}).toPromise();
  }

  async declineInvitationV2(token: string) {
    const api = await this.getApi();
    return this.httpClient.put(api.getRoute('cfi-student-declineInvitation').replace('{uuid}', token), {}).toPromise();
  }

  calculatePercentComplete(progress, elements) {

    const result = {
      writtenExamEligible: false,
      totalNonQuizPercentComplete: 0,
      requiredElements: [],
      optionalElements: []
    } as {
      writtenExamEligible: any;
      totalNonQuizPercentComplete: any;
      requiredElements: Array<any>;
      optionalElements: Array<any>;
    };

    if (progress) {
      result.writtenExamEligible = progress.writtenExamEligible;

      result.requiredElements = [
        ...elements?.videos.filter(video => video.required).map(video => ({ type: 'video', video: video })),
        ...elements?.videoSegments.filter(videoSegment => videoSegment.required).map(videoSegment => ({
          type: 'videoSegment',
          videoSegment: videoSegment
        })),
        ...elements?.documents.filter(document => document.required).map(document => ({ type: 'document', document: document })),
        ...elements?.quizzes.filter(quiz => quiz.required).map(quiz => ({ type: 'quiz', quiz: quiz })),
        ...elements?.lessons.filter(lesson => lesson.required).map(lesson => ({ type: 'lesson', lesson: lesson })),
      ];

      result.optionalElements = [
        ...elements?.videos.filter(video => !video.required).map(video => ({ type: 'video', video: video })),
        ...elements?.videoSegments.filter(videoSegment => !videoSegment.required).map(videoSegment => ({
          type: 'videoSegment',
          videoSegment: videoSegment
        })),
        ...elements?.documents.filter(document => !document.required).map(document => ({ type: 'document', document: document })),
        ...elements?.quizzes.filter(quiz => !quiz.required).map(quiz => ({ type: 'quiz', quiz: quiz })),
        ...elements?.lessons.filter(lesson => !lesson.required).map(lesson => ({ type: 'lesson', lesson: lesson })),
      ];

      const completedNonQuizElements = result.requiredElements
        .filter(element => element.type !== 'quiz' && element[element.type]?.completed).length;

      const requiredNonQuizElements = result.requiredElements
        .filter(element => element.type !== 'quiz').length;

      result.totalNonQuizPercentComplete = Math.floor(100 * completedNonQuizElements / requiredNonQuizElements) / 1;

    }
    return result;
  }

}


